import { create } from 'zustand';

interface LoadingMessage {
  id: string;
  title: string;
  description: string;
}

interface LoadingState {
  loading: boolean;
  title: string;
  description: string;
  activeRequests: number;
  messageQueue: LoadingMessage[];
}

interface LoadingStore extends LoadingState {
  startLoading: (title?: string, description?: string) => string;
  stopLoading: (requestId?: string) => void;
}

let requestIdCounter = 0;

const useLoadingStore = create<LoadingStore>((set, _get) => ({
  loading: false,
  title: '',
  description: '',
  activeRequests: 0,
  messageQueue: [],

  startLoading: (title = '', description = '') => {
    const requestId = `req-${++requestIdCounter}`;
    const newMessage: LoadingMessage = { id: requestId, title, description };

    set((state) => ({
      loading: true,
      activeRequests: state.activeRequests + 1,
      messageQueue: [...state.messageQueue, newMessage],
      // Show the new message (most recent request)
      title,
      description,
    }));

    return requestId;
  },

  stopLoading: (requestId?: string) => {
    set((state) => {
      const newActiveRequests = Math.max(0, state.activeRequests - 1);

      // Remove the message for this request from the queue
      const newQueue = requestId
        ? state.messageQueue.filter((msg) => msg.id !== requestId)
        : state.messageQueue.slice(0, -1); // Remove last if no ID provided

      // If there are still active requests, show the most recent remaining message
      if (newActiveRequests > 0 && newQueue.length > 0) {
        const currentMessage = newQueue[newQueue.length - 1];
        return {
          activeRequests: newActiveRequests,
          messageQueue: newQueue,
          loading: true,
          title: currentMessage.title,
          description: currentMessage.description,
        };
      }

      // No more active requests, hide the loader
      return {
        loading: false,
        title: '',
        description: '',
        activeRequests: 0,
        messageQueue: [],
      };
    });
  },
}));

export { useLoadingStore };
